import { computed, ref, watch } from 'vue'
import merge from 'lodash/merge'

import { events$ } from '@shared/services'

import { setLangTranslations } from '@shared/plugins/i18n'

import { CookieUtil } from '@shared/utils/Cookie'
import { logInfo } from '@shared/helpers/Logger'

import partnerStore from '@shared/store/partner'
import sessionStore from '@shared/store/session'
import useAuth from '@shared/hooks/useAuth'
import useBasket from '@shared/hooks/useBasket'
import useChatBot from '@shared/hooks/useChatBot'
import useI18n from '@shared/hooks/useI18n'

import {
  DEFAULT_DELAY_TIME,
  PACKAGE,
  STORAGE_KEY,
  REENTRY_PREFIX_DVP,
  REENTRY_PREFIX_OA,
  REENTRY_PREFIX_IQ,
  SESSION_GROUP,
} from '@shared/config/constants'
import { EVENT_LANGUAGE } from '@shared/config/events'

// GLOBAL DATA
const scriptSrcAttr = 'script[src="'
const languageLoaded = ref(false)
const contentIsLoading = ref(true)
//const isTestEnv = import.meta.env.VITE_ENV_KEY !== 'PRD'

// EVENTS
events$.once(EVENT_LANGUAGE.LOADED, () => {
  languageLoaded.value = true
})

export default function useApplication() {
  // HOOKS
  const { restartApplication } = useAuth()
  const { getContent, selectedLanguage, updateHtmlPageData } = useI18n()
  const { updateLanguage } = useBasket()
  const { initChatBot } = useChatBot()

  // COMPUTED
  const isContentReady = computed(() => {
    return !contentIsLoading.value
  })

  const isReadyWithoutPartner = computed(() => {
    return languageLoaded.value && sessionStore.isActive.value
  })

  const isReentryReadyWithoutActiveSession = computed(() => {
    return languageLoaded.value && !sessionStore.isActive.value && !sessionStore.session.basketId
  })

  const isReadyWithPartner = computed(() => {
    return languageLoaded.value && sessionStore.isActive.value && partnerStore.installed.value
  })

  const isWww = computed(() => {
    return document.body.dataset.trackApp !== import.meta.env.VITE_APP_ID
  })

  // METHODS
  async function changeLanguage({
    app = window._appName,
    event = EVENT_LANGUAGE.LOADED,
    language = selectedLanguage.value,
    additionalApps = [],
  }) {
    let content = {}
    /*
     * @note: app needs to be loaded last, so we can overwrite content on a need-basis.
     * e.g. aide has different content for a product
     */
    const contentFiles = [PACKAGE.ERRORS, PACKAGE.BASIC, ...additionalApps, app]
    let hasContentChanged = false

    for (const contentFile of contentFiles) {
      const languageChanged = language !== selectedLanguage.value
      const _contentFile = await getContent({ app: contentFile, language, languageChanged })
      if (_contentFile.changed) hasContentChanged = true
      merge(content, _contentFile.content)
    }

    if (hasContentChanged) {
      setLangTranslations(language, content)

      await updateLanguage({ language })
      updateHtmlPageData({ language, app })

      events$.emit(event, { language })

      if (contentIsLoading.value) {
        contentIsLoading.value = false
      }
    }
  }

  function dispatchCustomEvent(event, params = null) {
    const options = params ? { detail: { ...params } } : null

    logInfo(['%cCustomEvent', 'color: purple', event, options])
    window.dispatchEvent(new CustomEvent(event, options))
  }

  function getDvpUrl(language) {
    return import.meta.env.VITE_SANITAS_DVP_PATH + '/' + language?.toLowerCase()
  }

  function getRoutingTarget(to) {
    return `/${selectedLanguage.value.toLowerCase()}/` + to
  }

  function hashCode(str) {
    return Array.from(str).reduce((s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0, 0)
  }

  /**
   * Add event listener to konami and clear code for restart application
   *
   * Konami Code uppercase -1341396303
   * Konami Code lowercase -715050255
   * CLEAR-ENTER 1462084291
   * clear-ENTER 618617699
   */
  function initializeClearSequence() {
    let buffer = []
    let lastInput = Date.now()

    document.addEventListener('keydown', event => {
      const current = Date.now()
      if (current - lastInput > 1000) {
        buffer = []
      }

      buffer.push(event.key)
      lastInput = current

      const hasRightLength = buffer.length === 11 || buffer.length === 6
      if (!hasRightLength) {
        return
      }
      const isRightCode = [-1341396303, -715050255, 1462084291, 618617699].indexOf(hashCode(buffer.toString())) > -1
      if (isRightCode) {
        restartApplication()
      }
    })
  }

  function initProgressEvent(setValue) {
    let value = 0
    let progress = 0
    let step = 0.05
    const progressInterval = setInterval(() => {
      value += step
      progress = Math.round((Math.atan(value) / (Math.PI / 2)) * 60 * 1000) / 1000
      setValue(progress)
      if (progress >= 55) {
        clearInterval(progressInterval)
      } else if (progress >= 25) {
        step = 0.01
      }
    }, DEFAULT_DELAY_TIME / 10)
    return progressInterval
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      let shouldAppend = false
      let el = document.querySelector(scriptSrcAttr + src + '"]')
      if (!el) {
        el = document.createElement('script')
        el.type = 'text/javascript'
        el.async = true
        el.src = src
        shouldAppend = true
      } else if (el.hasAttribute('data-loaded')) {
        resolve(el)
        return
      }

      el.addEventListener('error', reject)
      el.addEventListener('abort', reject)
      el.addEventListener('load', function loadScriptHandler() {
        el.setAttribute('data-loaded', true)
        resolve(el)
      })

      if (shouldAppend) document.head.appendChild(el)
    })
  }

  /**
   * @param string selector     Selector about the reference
   * @returns {Promise}
   */
  function pendingHTML(selector) {
    const getter = __selector => {
      if (__selector[0] === '#') {
        return document.getElementById(__selector.substring(1))
      } else if (__selector[0] === '.') {
        return document.getElementsByClassName(__selector.substring(1))
      }
      return document.getSelection(__selector)
    }

    return new Promise((resolve, reject) => {
      const elements = getter(selector)
      if (elements?.id || elements?.length > 0) {
        return resolve(elements)
      }

      let tries = 0
      const poll = setInterval(() => {
        const __elements = getter(selector)
        if (__elements?.id || __elements?.length > 0) {
          clearInterval(poll)
          resolve(__elements)
        }
        tries++
        if (tries > 10) {
          clearInterval(poll)
          reject()
        }
      }, DEFAULT_DELAY_TIME / 5)
    })
  }

  function preferedColorScheme(value) {
    const key = STORAGE_KEY + '-color-scheme'

    if (value) {
      CookieUtil.set({
        key,
        value,
      })
    }

    let colorScheme = CookieUtil.read(key)

    if (!colorScheme) {
      colorScheme = 'light'
    }

    /*
    if (!colorScheme && isTestEnv) {
      colorScheme = window.matchMedia(`(prefers-color-scheme: dark)`) ? 'dark' : 'light'
    } else if (!colorScheme) {
      colorScheme = 'light'
    }
    */

    return colorScheme
  }

  function setWindowAppGroupAndAppKey(reentryToken) {
    const tokenPrefix = reentryToken.slice(0, 2)
    if (tokenPrefix === REENTRY_PREFIX_DVP || tokenPrefix === REENTRY_PREFIX_OA) {
      window._appGroup = SESSION_GROUP.OFFER
      return 'DVP'
    } else if (tokenPrefix === REENTRY_PREFIX_IQ) {
      window._appGroup = SESSION_GROUP.QUOTE
      return 'COFFER'
    }
  }

  function unloadScript(src) {
    return new Promise(function (resolve, reject) {
      const el = document.querySelector(scriptSrcAttr + src + '"]')

      if (!el) {
        reject()
        return
      }

      document.head.removeChild(el)

      resolve()
    })
  }

  // WATCHER
  watch(
    () => isReadyWithPartner.value,
    (val) => {
      if (import.meta.env.VITE_FEATURE_FLAG_CHATBOT && !isWww.value && val) {
        initChatBot()
      }
    }
  )

  return {
    isContentReady,
    isReadyWithoutPartner,
    isReadyWithPartner,
    isReentryReadyWithoutActiveSession,
    isWww,

    changeLanguage,
    dispatchCustomEvent,
    getDvpUrl,
    getRoutingTarget,
    hashCode,
    initializeClearSequence,
    initProgressEvent,
    loadScript,
    pendingHTML,
    preferedColorScheme,
    setWindowAppGroupAndAppKey,
    unloadScript,
  }
}
